import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart, Search, Menu, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { getCartCount, wishlist } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
            <h1 className="text-2xl md:text-3xl font-light tracking-tighter text-[#D4AF37]">
              Savistones
            </h1>
          </Link>

          <nav className="hidden md:flex items-center space-x-8">
            <Link to="/products" className="text-sm uppercase tracking-wider text-[#A3A3A3] hover:text-white transition-colors" data-testid="products-nav-link">
              Collections
            </Link>
            <Link to="/products?featured=true" className="text-sm uppercase tracking-wider text-[#A3A3A3] hover:text-white transition-colors" data-testid="featured-nav-link">
              Featured
            </Link>
            {user?.role === 'admin' && (
              <Link to="/admin" className="text-sm uppercase tracking-wider text-[#D4AF37] hover:text-[#F1E5AC] transition-colors" data-testid="admin-nav-link">
                Admin
              </Link>
            )}
          </nav>

          <div className="flex items-center space-x-6">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="text-[#A3A3A3] hover:text-white transition-colors"
              data-testid="search-button"
            >
              <Search size={20} />
            </button>

            <Link to="/wishlist" className="relative text-[#A3A3A3] hover:text-white transition-colors" data-testid="wishlist-link">
              <Heart size={20} />
              {wishlist.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#D4AF37] text-black text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                  {wishlist.length}
                </span>
              )}
            </Link>

            <Link to="/cart" className="relative text-[#A3A3A3] hover:text-white transition-colors" data-testid="cart-link">
              <ShoppingCart size={20} />
              {getCartCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#D4AF37] text-black text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                  {getCartCount()}
                </span>
              )}
            </Link>

            {user ? (
              <button
                onClick={handleLogout}
                className="hidden md:block text-sm uppercase tracking-wider text-[#A3A3A3] hover:text-white transition-colors"
                data-testid="logout-button"
              >
                Logout
              </button>
            ) : (
              <Link
                to="/admin/login"
                className="hidden md:block text-sm uppercase tracking-wider text-[#A3A3A3] hover:text-white transition-colors"
                data-testid="login-link"
              >
                Admin
              </Link>
            )}

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="py-4 border-t border-white/10">
            <input
              type="text"
              placeholder="Search for jewelry..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearch}
              autoFocus
              className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white px-0 py-3 outline-none transition-colors"
              data-testid="search-input"
            />
          </div>
        )}
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden border-t border-white/10 bg-[#0A0A0A]" data-testid="mobile-menu">
          <nav className="flex flex-col space-y-4 px-6 py-6">
            <Link to="/products" className="text-sm uppercase tracking-wider text-[#A3A3A3] hover:text-white transition-colors">
              Collections
            </Link>
            <Link to="/products?featured=true" className="text-sm uppercase tracking-wider text-[#A3A3A3] hover:text-white transition-colors">
              Featured
            </Link>
            {user?.role === 'admin' && (
              <Link to="/admin" className="text-sm uppercase tracking-wider text-[#D4AF37] hover:text-[#F1E5AC] transition-colors">
                Admin
              </Link>
            )}
            {user ? (
              <button onClick={handleLogout} className="text-left text-sm uppercase tracking-wider text-[#A3A3A3] hover:text-white transition-colors">
                Logout
              </button>
            ) : (
              <Link to="/admin/login" className="text-sm uppercase tracking-wider text-[#A3A3A3] hover:text-white transition-colors">
                Admin Login
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;